import { FormSelect, PrimaryButton } from '@systemeio/ui'
import { addCourse } from 'modules/courses/bundles/course-bundle/api/course-bundle-api'
import { useCoursesList } from 'modules/courses/bundles/course-bundle/hooks/use-courses-list'
import { CourseBundleItemInterface } from 'modules/courses/bundles/course-bundle/types/course-bundle-interface'
import React, { useState } from 'react'
import CreateModal from 'shared/components/create-modal'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { AddIcon } from '@systemeio/ui'

interface AddCourseEditInterface {
  course?: number
}

const defaultValues: AddCourseEditInterface = {
  course: undefined,
}

interface AddCourseModalProps {
  addCourseWithUpdate: (course: CourseBundleItemInterface) => Promise<void>
  courseBundleId: string
  disabled: boolean
  withIcon?: boolean
}

const AddCourseModal = ({
  addCourseWithUpdate,
  courseBundleId,
  disabled,
  withIcon = true,
}: AddCourseModalProps) => {
  const { t } = useLocoTranslation()
  const [opened, setOpened] = useState(false)
  const onClose = () => setOpened(false)

  const { courseList, mutate: mutateCourseList } = useCoursesList(courseBundleId)

  const onCreate = async (data: AddCourseEditInterface) => {
    const courseInfo = courseList?.find(course => course.id === data.course)
    if (courseInfo) {
      const newCourseData = {
        course: courseInfo.id,
      }
      try {
        const response = await addCourse(courseBundleId, newCourseData)
        const addedCourseBundleItem: CourseBundleItemInterface = {
          id: response.data.id,
          delayBeforeStart: 0,
          course: {
            id: response.data.course.id,
            name: response.data.course.name,
          },
        }
        await addCourseWithUpdate(addedCourseBundleItem)
        await mutateCourseList(data => {
          if (data) {
            return data.filter(course => course.id !== response.data.course.id)
          }
        }, false)
      } catch (e) {
        throw e
      }
    }
  }

  const courseSelectData = courseList?.map(course => {
    return {
      id: course.id,
      caption: course.name,
    }
  })

  return (
    <>
      <PrimaryButton
        disabled={disabled}
        className="flex items-center justify-between"
        onClick={() => setOpened(true)}
        isFetching={!courseList}
      >
        <AddIcon className={`${withIcon ? 'block' : 'lg:hidden'} block`} />
        <span className="hidden whitespace-nowrap lg:block">
          {t('dashboard.course_bundle.add_course_title')}
        </span>
      </PrimaryButton>
      <CreateModal
        opened={opened}
        onClose={onClose}
        defaultValues={defaultValues}
        fullCaption={t('dashboard.course_bundle.add_course_title')}
        creatable={[
          {
            field: 'course',
            onRender: (value, onChange) => (
              <FormSelect
                key={'course-selector'}
                data={courseSelectData}
                placeholder={t('dashboard.course_bundle.choose_course')}
                value={value}
                onChange={data => onChange(data)}
              />
            ),
          },
        ]}
        onCreate={onCreate}
        requiredFields={['course']}
        needToast={false}
      />
    </>
  )
}

export default AddCourseModal
